// supervisor-attempt-state.ts
//
// Per-ticket RetryState (retry-policy.ts's counters) persisted as a single
// JSON file, keyed by ticket id. Same reasoning as quota-sleep-scheduler.ts's
// own file (Agent A QD 2): this is overwrite-latest state derived from
// attempt outcomes, not a fact that needs journal replay — extending
// mission-state-reducer.ts's delta vocabulary for it would be YAGNI.
//
// Also tracks `attempt_no` per ticket (physical_runs IS attempt_no — §6.4:
// "attempt tăng cả khi quota") so session ids/log file names never collide
// across retries of the same ticket.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { isDeadlineRateLimitCycle } from "./deadline-rate-limit-cap.js";
import { emptyRetryState, type RetryState } from "./retry-policy.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

export interface AttemptStateFile {
  tickets: Record<string, RetryState>;
  /**
   * Per-ticket quota backoff ladder position (quota-sleep-scheduler.ts's
   * `backoffIndex` input), persisted here rather than in quota-sleep.json
   * because that file only ever holds the CURRENT sleep (overwritten on the
   * next ticket) — without this, every quota retry would replay index 0 and
   * the ladder configured in `backoff_min` (§18) would never advance past
   * its first step across restarts or across the next tick's dispatch.
   */
  quotaBackoffIndex: Record<string, number>;
  /** Consecutive deadline kills routed to quota-sleep as rate limits (deadline-rate-limit-cap.ts). */
  deadlineRateLimitStreak?: Record<string, number>;
}

function statePath(controlRoot: string): string {
  return join(controlRoot, "attempt-state.json");
}

/** Missing/corrupt file => empty state (never seen an attempt yet), never throws. */
export async function readAttemptStateFile(controlRoot: string): Promise<AttemptStateFile> {
  try {
    const raw = await fs.readFile(statePath(controlRoot), "utf8");
    const parsed = JSON.parse(raw) as Partial<AttemptStateFile>;
    return {
      tickets: parsed.tickets ?? {},
      quotaBackoffIndex: parsed.quotaBackoffIndex ?? {},
      deadlineRateLimitStreak: parsed.deadlineRateLimitStreak ?? {},
    };
  } catch {
    return { tickets: {}, quotaBackoffIndex: {} };
  }
}

export async function writeAttemptStateFile(
  controlRoot: string,
  state: AttemptStateFile,
): Promise<void> {
  await fs.mkdir(controlRoot, { recursive: true });
  const tmp = `${statePath(controlRoot)}.tmp-${process.pid}`;
  await fs.writeFile(tmp, `${JSON.stringify(state, null, 2)}\n`, "utf8");
  await fs.rename(tmp, statePath(controlRoot));
}

/** Retry state for one ticket, or a fresh zeroed state if never attempted. */
export function retryStateFor(state: AttemptStateFile, ticketId: string): RetryState {
  return state.tickets[ticketId] ?? emptyRetryState();
}

/** Pure update — caller persists via `writeAttemptStateFile`. */
export function withRetryState(
  state: AttemptStateFile,
  ticketId: string,
  next: RetryState,
): AttemptStateFile {
  return { ...state, tickets: { ...state.tickets, [ticketId]: next } };
}

/** Quota backoff ladder position for one ticket; 0 if it has never slept. */
export function quotaBackoffIndexFor(state: AttemptStateFile, ticketId: string): number {
  return state.quotaBackoffIndex[ticketId] ?? 0;
}

/** Pure update — caller persists via `writeAttemptStateFile`. */
export function withQuotaBackoffIndex(
  state: AttemptStateFile,
  ticketId: string,
  next: number,
): AttemptStateFile {
  return { ...state, quotaBackoffIndex: { ...state.quotaBackoffIndex, [ticketId]: next } };
}

/** Consecutive deadline rate-limit quota-sleeps for one ticket; 0 if none. */
export function deadlineRateLimitStreakFor(state: AttemptStateFile, ticketId: string): number {
  return state.deadlineRateLimitStreak?.[ticketId] ?? 0;
}

/**
 * Pure update after routing: +1 when this deadline rate-limit quota-slept;
 * unchanged for any other deadline kill — so once the cap demoted one to a
 * timeout, every later one stays a timeout (review 261006 L9); back to 0 only
 * on an attempt that did not end at the deadline.
 */
export function withDeadlineRateLimitStreakAfterRoute(
  state: AttemptStateFile,
  ticketId: string,
  cycle: AttemptCycleResult,
  route: RouteOutcomeResult,
): AttemptStateFile {
  const prior = deadlineRateLimitStreakFor(state, ticketId);
  const endedAtDeadline =
    cycle.kind === "verified" && cycle.sessionResult.outcome === "deadline_exceeded";
  const next =
    isDeadlineRateLimitCycle(cycle) && route.kind === "quota_sleep"
      ? prior + 1
      : endedAtDeadline
        ? prior
        : 0;
  return {
    ...state,
    deadlineRateLimitStreak: { ...state.deadlineRateLimitStreak, [ticketId]: next },
  };
}
